import axiosInstance from "../components/api/axiosInstance";

/**
 * Fetch project users by project ID (and optionally subproject ID).
 * GET /project-users?project_id=X
 * GET /project-users?project_id=X&sub_project_id=Y
 * @param {number|string} projectId - Project ID
 * @param {number|string|null} subProjectId - Optional subproject ID for subproject-specific users
 * @returns {Promise<Array>} Array of { User_Id, User_Name, Added_At }
 */
export async function fetchProjectUsers(projectId, subProjectId = null) {
  if (!projectId) return [];
  try {
    const params = { project_id: Number(projectId) };
    if (subProjectId != null && subProjectId !== undefined && subProjectId !== "") {
      params.sub_project_id = Number(subProjectId);
    }
    const response = await axiosInstance.get("/project-users", { params });
    const raw = response?.data;
    const users = raw?.Users ?? raw?.users ?? (Array.isArray(raw) ? raw : []);
    return Array.isArray(users) ? users : [];
  } catch (err) {
    console.error("Error fetching project users:", err);
    return [];
  }
}

/**
 * Fetch task statuses.
 * GET /task-statuses
 * @returns {Promise<Array>} Array of { Status_Id, Status, Description }
 */
export async function fetchTaskStatuses() {
  const response = await axiosInstance.get("/task-statuses");
  const raw = response.data;
  if (Array.isArray(raw)) return raw;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.task_statuses && Array.isArray(raw.task_statuses)) return raw.task_statuses;
  return [];
}

/**
 * Fetch tasks for a user story.
 * GET /tasks?project_id=&sub_project_id=&story_id=&status_id=&priority=
 * @param {Object} params - { storyId, projectId, subProjectId?, statusId?, priority? }
 * @returns {Promise<Array>} Array of tasks
 */
export async function fetchTasksByStoryId(storyId, projectId, subProjectId = null, statusId = null, priority = null) {
  if (!storyId || !projectId) return [];
  const params = {
    project_id: Number(projectId),
    story_id: Number(storyId)
  };
  if (subProjectId != null && subProjectId !== undefined && subProjectId !== "") {
    params.sub_project_id = Number(subProjectId);
  }
  if (statusId != null && statusId !== undefined && statusId !== "") {
    params.status_id = Number(statusId);
  }
  if (priority != null && priority !== undefined && priority !== "") {
    params.priority = priority;
  }
  const response = await axiosInstance.get("/tasks", { params });
  const raw = response.data;
  if (Array.isArray(raw)) return raw;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.tasks && Array.isArray(raw.tasks)) return raw.tasks;
  return [];
}

/**
 * Fetch a single task by ID.
 * GET /tasks/:taskId
 * @param {number} taskId - Task ID
 * @returns {Promise<Object>} Task object
 */
export async function fetchTaskById(taskId) {
  if (!taskId) return null;
  const response = await axiosInstance.get(`/tasks/${taskId}`);
  return response.data?.data ?? response.data;
}

/**
 * Fetch all tasks (for dropdown selections).
 * GET /tasks
 * @returns {Promise<Array>} Array of tasks
 */
export async function fetchAllTasks() {
  const response = await axiosInstance.get("/tasks");
  const raw = response.data;
  if (Array.isArray(raw)) return raw;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.tasks && Array.isArray(raw.tasks)) return raw.tasks;
  return [];
}

/**
 * Fetch tasks for a project (optional subproject filter).
 * GET /tasks?project_id=&sub_project_id=
 * @param {number|string} projectId
 * @param {number|string|null|undefined} subProjectId - omit or null for project-level-only query params
 * @returns {Promise<Array>}
 */
export async function fetchTasksByProject(projectId, subProjectId = null) {
  if (!projectId) return [];
  const params = { project_id: Number(projectId) };
  if (subProjectId != null && subProjectId !== undefined && subProjectId !== "") {
    params.sub_project_id = Number(subProjectId);
  }
  const response = await axiosInstance.get("/tasks", { params });
  const raw = response.data;
  if (Array.isArray(raw)) return raw;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.tasks && Array.isArray(raw.tasks)) return raw.tasks;
  return [];
}

/**
 * Update a task.
 * PUT /update-task/:taskId
 * @param {number} taskId - Task ID
 * @param {Object} payload - { Project_Id, Sub_Project_Id, Story_Id, Title, Description, Start_Date, End_Date, Priority, Updated_By }
 * @returns {Promise<Object>} Updated task
 */
export async function updateTask(taskId, payload) {
  const response = await axiosInstance.put(`/update-task/${taskId}`, payload);
  return response.data?.data ?? response.data;
}

/**
 * Delete a task.
 * DELETE /task/:taskId
 * @param {number} taskId - Task ID
 * @returns {Promise<Object>} Response with message
 */
export async function deleteTask(taskId) {
  const response = await axiosInstance.delete(`/task/${taskId}`);
  return response.data;
}

/**
 * Create a new task.
 * POST /create-task
 * @param {Object} payload - { Project_Id, Sub_Project_Id?, Story_Id, Title, Description, Start_Date, End_Date, Created_By, Assigned_User_Ids? }
 * @returns {Promise<Object>} Created task
 */
export async function createTask(payload) {
  const response = await axiosInstance.post("/create-task", payload);
  return response.data?.data ?? response.data ?? response;
}
